import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Header } from './layout/Header';
import { Footer } from './layout/Footer';
import { useToast } from './Toast';
import { HomeBanners } from './HomeBanners';
import { ComposeBox } from './ComposeBox';
import { getBackendUrl } from '../utils/apiUrl';
import { prepareGeneration } from '../utils/sessionManager';
import { detectModeHeuristic } from '../utils/modeHeuristic';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Sequential Text Animation Component with Continuous Loop
const SequentialText = ({ text }) => {
  // V24.78: REMOVED animation - text is now fully static on desktop
  // No blinking, motion, or transition effects
  return (
    <span 
      style={{ 
        display: 'inline',
        whiteSpace: 'normal',
        minWidth: '0',
        maxWidth: '100%',
        wordWrap: 'break-word'
      }}
    >
      {text}
    </span>
  );
};

export const Home = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, refreshUser, login, isProcessingOAuth } = useAuth();
  
  // Detect if app is running in iframe (like Emergent preview window)
  const [isInIframe, setIsInIframe] = useState(false);
  const [showIframeBanner, setShowIframeBanner] = useState(true);
  
  useEffect(() => {
    // Check if running in iframe
    if (window.self !== window.top) {
      setIsInIframe(true);
    }
    
    // SCROLL & REFRESH FIX: Always reset scroll position to top on component mount
    // This prevents incorrect rendering when page is refreshed while scrolled
    window.scrollTo(0, 0);
    
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  
  // ==================== BINARY BACKGROUND GENERATOR ====================
  const generateBinaryBackground = useCallback(() => {
    const rows = 50; // Number of rows
    const cols = 120; // Number of columns
    let matrix = '';
    
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        // Random 0 or 1 with AI-like distribution
        matrix += Math.random() > 0.5 ? '1' : '0';
        matrix += ' '; // Add space between characters
      }
      matrix += '\n'; // New line after each row
    }
    
    return matrix;
  }, []);
  
  const binaryMatrix = useMemo(() => generateBinaryBackground(), [generateBinaryBackground]);
  // ==================== END BINARY BACKGROUND GENERATOR ====================
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(null); // null until loaded
  const [useStreaming] = useState(true);
  const [detectionBanner, setDetectionBanner] = useState(null); // { mode, message }
  const [outOfScopeBanner, setOutOfScopeBanner] = useState(null); // { title, description, domains, instruction }
  const [warningBanner, setWarningBanner] = useState(null); // { message } - for unrelated input
  const [charLimitWarning, setCharLimitWarning] = useState(false); // Character limit exceeded warning
  const [detecting, setDetecting] = useState(false);

  // V37.43.5 — Parity with backend V37.40.1 strict bug-first precedence.
  // Uses the canonical `detectModeHeuristic` (utils/modeHeuristic.js), which
  // mirrors the backend's deterministic order: failure flag → bug_fixing,
  // negated bug → new_project, bug intent (single verb: fix/debug/resolve/…)
  // → bug_fixing, project intent → new_project. Any single bug keyword is
  // sufficient — no 2-hit threshold, no 12-char min length (both were the
  // prior gap that caused "Fix the login button" to misroute).
  const looksLikeBug = useMemo(() => {
    return detectModeHeuristic(input || '') === 'bug_fixing';
  }, [input]);
  
  // Mobile detection for responsive placeholder text
  const [isMobileView, setIsMobileView] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // V24.99.21: Smart Question Flow states REMOVED - Direct generation flow
  // Execution path: Input → Generation → Final Result → Refine (if triggered)
  
  // Ref for textarea to handle scroll boundary behavior
  const textareaRef = React.useRef(null);
  
  const isSignedIn = useMemo(() => !!user, [user]);
  
  const MAX_CHAR_HOME = useMemo(() => isSignedIn ? 10000 : 1500, [isSignedIn]);
  
  // Fork guide detection COMPLETELY DISABLED - removed to prevent confusion
  
  // Use toast notification hook
  const { ToastContainer } = useToast();
  
  // Preload workflow components on mount (performance optimization)
  useEffect(() => {
    const preloadWorkflow = async () => {
      const workflowPreloader = (await import('../utils/workflowPreloader')).default;
      workflowPreloader.scheduleIdlePrefetch(() => {
        workflowPreloader.preloadGenerationWorkflow();
      });
    };
    preloadWorkflow();
  }, []);
  
  useEffect(() => {
    // RACE CONDITION FIX: Wait for auth to FULLY finish loading before setting credits
    if (authLoading) {
      // Keep credits as null while loading - prevents flash of zero credit warning
      return;
    }
    
    // Use user credits if authenticated AND user object has credits loaded
    if (user && user.credits !== undefined) {
      setCredits(user.credits);
    } else if (isAuthenticated && (!user || user.credits === undefined)) {
      // User is authenticated but credits not loaded yet - keep null to prevent flash
      // Don't set to 0 here - wait for user object to have credits
      return;
    } else if (!isAuthenticated) {
      // Only set to 0 when we're CERTAIN user is not authenticated
      setCredits(0.00);
    }
  }, [user, isAuthenticated, authLoading]);
  
  // Check for out-of-scope banner on mount/navigation
  useEffect(() => {
    const outOfScopeInfo = sessionStorage.getItem('ep_out_of_scope_info');
    if (outOfScopeInfo) {
      try {
        const info = JSON.parse(outOfScopeInfo);
        setOutOfScopeBanner(info);
        sessionStorage.removeItem('ep_out_of_scope_info'); // Clear after showing
      } catch (e) {
        console.error('[Out of Scope] Failed to parse info:', e);
      }
    }
  }, []);
  
  const handleGenerate = useCallback(async () => {
    if (!input.trim()) return;
    
    // AUTH CHECK: Require authentication for prompt generation
    if (!isAuthenticated) {
      setWarningBanner({
        type: 'auth',
        message: 'Please log in to generate prompts'
      });
      // Scroll to top so user can see the banner
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Clear any other warning banners when authenticated user tries again
    setWarningBanner(null);
    
    // V37.44.8 — Free access. Credit-balance gate removed; the 10-gen /
    // 24-h cap is now the only frontend pre-flight. Backend re-checks
    // authoritatively via _check_daily_limit_or_429 and returns 429 with
    // detail "daily_limit_reached" if the user has already hit the cap.
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const _statusResp = await fetch(`${backendUrl}/api/generation-limit-status`, {
        credentials: 'include'
      });
      if (_statusResp.ok) {
        const _st = await _statusResp.json();
        // V37.44.14 — Superuser exemption: API returns {exempt:true,
        // remaining:null} for accounts in SUPERUSER_EXEMPT_EMAILS. Never
        // block them client-side. Also guard against non-numeric
        // `remaining` (null coerces to 0 in `remaining <= 0`, which was
        // spuriously firing the cap banner for exempt users).
        if (_st && _st.authenticated && _st.exempt !== true && typeof _st.remaining === 'number' && _st.remaining <= 0) {
          setWarningBanner({
            type: 'limit',
            message: "You've reached your 10 free generations for the past 24 hours. Please come back a little later.",
            actionLabel: null,
            onAction: null
          });
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }
    } catch (_e) { /* non-fatal — server 429 will still block */ }
    
    // Clear previous completed result from session storage
    sessionStorage.removeItem('ep_completed_result');
    
    // NOTE: Question flow completion now automatically triggers generation
    // This handleGenerate function is only called for initial flow (before questions)
    
    setLoading(true);
    setDetecting(true);

    try {
      // V37.43.6 — INTENT-FIRST VALIDATION ORDERING.
      // If detectModeHeuristic resolves a definite mode (bug_fixing or
      // new_project), the input has clear software intent and the relevance
      // gate is skipped entirely. Only undecided inputs (heuristic === null)
      // run through validate-input + the local tech-signal deny-check.
      // Reuses V37.42.1 detectModeHeuristic — no new heuristics, no backend
      // change, no UI change.
      const heuristicMode = detectModeHeuristic(input || '');
      const intentResolved = heuristicMode === 'bug_fixing' || heuristicMode === 'new_project';

      let message = null;
      if (!intentResolved) {
        // V37.43.4 — VALIDATION GATE for ambiguous inputs only.
        const validationResponse = await axios.post(`${API}/prompt/validate-input`, {
          user_input: input
        }, {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        });

        const { is_relevant, confidence: validationConfidence, message: backendMsg } = validationResponse.data;
        message = backendMsg;

        // Client-side deny check — only STRONG tech signals (generic verbs
        // excluded from TECH_KW; require verb+tech-noun via TECH_NOUN_AFTER_VERB).
        const TECH_KW = /\b(api|app|application|css|database|deploy(?:ed|ment)?|docker|frontend|backend|function|git|github|gitlab|html|http|install|javascript|json|kubernetes|library|linux|module|npm|yarn|pnpm|package|python|react|vue|angular|svelte|rest|graphql|server|sql|typescript|ui|ux|webhook|website|site|aws|azure|gcp|cloud|devops|component|endpoint|framework|integration|microservice|mongodb|postgres|mysql|redis|route|schema|sdk|software|stack|token|variable|debug(?:ging)?|bug|crash|broken|performance|optimi[sz]e|authentication|authorization|middleware|plugin|config|ios|android|flutter|swift|kotlin|java|saas|startup|mvp|prototype|architecture|algorithm|llm|gpt|chatgpt|openai|neural|machine\s+learning|ai\b|dashboard|portal|marketplace|chatbot|extension|widget|scrapy|scraper|webapp|mobile\s+app|web\s+app|desktop\s+app)\b/i;
        const TECH_NOUN_AFTER_VERB = /\b(?:build|create|scaffold|develop|design|implement|launch|deploy|clone|make|replicate|rebuild|prototype|code|write|generate|refactor)\s+(?:a|an|the|new|my|our|your|me\s+a|me\s+an|me\s+the)?\s*\b(?:app|application|platform|system|website|site|tool|dashboard|portal|service|marketplace|saas|mvp|startup|software|game|bot|chatbot|api|extension|plugin|widget|component|framework|sdk|page|backend|frontend|mobile\s+app|web\s+app|desktop\s+app|chrome\s+extension|browser\s+extension|landing\s+page|admin\s+panel|cms|crm|erp)\b/i;
        const FROM_SCRATCH = /\b(?:from\s+scratch|greenfield|boilerplate|clone\s+(?:youtube|uber|whatsapp|instagram|twitter|tiktok|facebook|spotify|netflix|amazon|airbnb|slack|discord|reddit|pinterest|linkedin|snapchat|zoom|shopify|stripe|paypal|notion|figma|canva))\b/i;
        const BUG_INTENT_LOCAL = /\b(?:fix(?:ing)?|error|exception|crash(?:ed|ing)?|broken|not\s+working|doesn'?t\s+work|stopped\s+working|bug|regression|traceback|stack\s+trace|500\s+error|404|npm\s+err|build\s+error|compile\s+error|syntax\s+error|type\s+error|undefined\s+is\s+not)\b/i;
        const hasLocalTechSignal = TECH_KW.test(input) || TECH_NOUN_AFTER_VERB.test(input) || FROM_SCRATCH.test(input) || BUG_INTENT_LOCAL.test(input);

        // Block when: backend rejected (legacy path), OR backend accepted but
        // the verdict is the permissive short-input fallback AND zero local tech signal.
        const shouldBlock = (!is_relevant && validationConfidence > 0.7) || (is_relevant && !hasLocalTechSignal);

        if (shouldBlock) {
          setLoading(false);
          setDetecting(false);
          setWarningBanner({
            message: message || "This request seems unrelated to building software, apps, or system prompts. ElonPrompt AI is designed to help you generate, refine, or debug technical and coding-related prompts. Please enter a project or system-related instruction."
          });
          return;
        }
      }

      // V37.24.8 — UNIFIED SILENT MODE ALIGNMENT. Mode resolved AFTER gate.
      // If heuristic decided, use it directly; otherwise fall back to the
      // looksLikeBug-derived default (preserves prior behavior).
      const mode = intentResolved ? heuristicMode : (looksLikeBug ? 'bug_fixing' : 'new_project');

      // V37.24.8: detect-mode call removed. Mode is the heuristic decision.
      setDetecting(false);

      prepareGeneration(Object.freeze({ input, mode, useStreaming }));
      if (user) await refreshUser();
      setLoading(false);
      navigate(`/result?streaming=true&mode=${mode}`);
    } catch (error) {
      console.error('Generation failed:', error);
      alert('Failed to process your request. Please try again.');
      setLoading(false);
      setDetecting(false);
      setDetectionBanner(null);
    }
  }, [input, isAuthenticated, credits, navigate, refreshUser, user, setWarningBanner, useStreaming, looksLikeBug]);
  
  // V24.99.21: Smart Question Flow handlers REMOVED
  // Direct generation flow: Input → Generation → Final Result → Refine

  
  
  // ==================== COMPOSE BOX SCROLL BOUNDARY HANDLER ====================
  // Fix for scroll-locking issue: Allow page scroll when textarea reaches boundaries
  const handleTextareaWheel = useCallback((event) => {
    const textarea = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = textarea;
    
    const isAtTop = scrollTop === 0;
    const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;
    
    const isScrollingUp = event.deltaY < 0;
    const isScrollingDown = event.deltaY > 0;
    
    // Allow event to bubble to page when at boundary
    if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
      // Don't prevent default - let the scroll propagate to the page
      return;
    }
    
    // Prevent page scroll when scrolling within textarea bounds
    event.stopPropagation();
  }, []);
  
  
  // ==================== END SCROLL BOUNDARY / UTILITY HANDLERS ====================
  
  // ==================== END UNIFIED HANDLERS ====================
  
  // Determine credit/prompt messaging based on user state
  const getPromptMessage = () => {
    // Hide promotional messages for all users (authenticated and unsigned)
    return {
      main: null,
      secondary: null,
      showSignIn: false,
      show: false
    };
  };
  
  const promptMessage = getPromptMessage();
  
  return (
    <div className="home-page-container" style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', backgroundColor: '#FAF9F6', position: 'relative'}}>
      {/* Binary AI Background - Homepage Only */}
      {/* SCROLL & REFRESH FIX: Binary background with explicit z-index to prevent overlay */}
      <div className="binary-background" aria-hidden="true" style={{ position: 'fixed', zIndex: 0, pointerEvents: 'none' }}>
        <pre>{binaryMatrix}</pre>
      </div>
      
      <ToastContainer />
      
      {/* IFRAME WARNING BANNER - Critical for authentication */}
      {isInIframe && showIframeBanner && !isAuthenticated && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-black px-6 py-3 z-50 shadow-lg">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">&#x26A0;&#xFE0F;</span>
              <div>
                <p className="font-bold text-sm">Authentication Required - Please Open in New Tab</p>
                <p className="text-xs">Google Sign-in doesn't work in preview windows. Click "Open in New Tab" to authenticate and use memory features.</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => window.open(window.location.href, '_blank')}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium text-sm"
              >
                Open in New Tab
              </button>
              <button
                onClick={() => setShowIframeBanner(false)}
                className="text-black hover:text-gray-700 text-xl font-bold"
              >
                &times;
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Header credits={credits} showAuth={true} showCredits={true} />
      
      {/* SCROLL & REFRESH FIX: Main content with explicit z-index to stay above binary background */}
      <main className="pb-6 md:pb-10 px-2 sm:px-4 md:px-6" style={{paddingTop: 'calc(56px + 24px)', transition: 'none', position: 'relative', zIndex: 1}}>
        <div className="max-w-6xl mx-auto">
          
          <div className="bg-white rounded-xl md:rounded-3xl shadow-2xl p-3 sm:p-5 md:p-8 border border-gray-300" style={{ position: 'relative', zIndex: 2 }}>
            <HomeBanners
              isAuthenticated={isAuthenticated} authLoading={authLoading} isProcessingOAuth={isProcessingOAuth}
              user={user} credits={credits} detecting={detecting} detectionBanner={detectionBanner}
              outOfScopeBanner={outOfScopeBanner} setOutOfScopeBanner={setOutOfScopeBanner}
              warningBanner={warningBanner} setWarningBanner={setWarningBanner}
              charLimitWarning={charLimitWarning} setCharLimitWarning={setCharLimitWarning}
              MAX_CHAR_HOME={MAX_CHAR_HOME} inputLength={input.length} navigate={navigate}
            />
            
            <div className="text-center -mb-2 md:mb-5 w-full px-1 md:px-0" style={{ position: 'relative', zIndex: 10 }}>
              {/* V37.44.30 — New hero copy; H2 tightened to text-[min(3.1vw,13px)]
                  so the 46-char headline stays on ONE line from 320px → 430px. */}
              <h2 className="text-[min(3.1vw,13px)] sm:text-xl md:text-3xl font-bold text-black mb-1.5 md:mb-2 leading-tight text-center px-1 whitespace-nowrap sm:whitespace-normal">
                Turn Your App, Web &amp; SaaS Idea Into Blueprints
              </h2>
              <p className="text-gray-700 font-semibold mb-1 tracking-wide text-center px-1" style={{ fontSize: '0.95em' }}>
                {/* V37.44.30 — mobile short + desktop full supporting line. */}
                <span className="text-xs sm:hidden">Transform ideas and bug fixes into specs.</span>
                <span className="hidden sm:inline sm:text-sm md:text-base">Transform ideas and bug fixes into build-ready specs.</span>
              </p>
              {/* V37.42.7 — mobile-only tagline below the subheadline.
                  Hidden from md+ to preserve desktop layout density.
                  V37.44.30 — copy: "Let our AI transform it." */}
              <p
                data-testid="home-mobile-tagline"
                className="md:hidden text-gray-500 font-bold mb-1 tracking-wide text-center px-1 leading-snug whitespace-nowrap"
              >
                Build or fix something? Let our AI transform it.
              </p>
              <p className="text-gray-500 text-[11px] sm:text-sm font-normal text-center px-3 md:px-4 leading-relaxed max-w-full mx-auto" style={{ 
                whiteSpace: 'normal', 
                overflow: 'hidden', 
                minHeight: '20px',
                display: 'block',
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }}>
                {/* Hidden on mobile, visible on desktop (md breakpoint and up) */}
                <span className="hidden md:inline-block" style={{ 
                  whiteSpace: 'normal',
                  maxWidth: '100%'
                }}>
                  <SequentialText text="Build or fix something? Let our AI transform it." />
                </span>
              </p>
            </div>
            
            {/* Upload component removed - file uploads now handled in 7-smart question flow only */}
            
            <ComposeBox
              textareaRef={textareaRef} input={input} setInput={setInput}
              isSignedIn={isSignedIn} warningBanner={warningBanner} setWarningBanner={setWarningBanner}
              charLimitWarning={charLimitWarning} setCharLimitWarning={setCharLimitWarning}
              MAX_CHAR_HOME={MAX_CHAR_HOME} handleTextareaWheel={handleTextareaWheel}
              isMobileView={isMobileView} loading={loading}
            />

            {/* Mode Detection Banner */}
            
            <button
              onClick={handleGenerate}
              disabled={loading || !input.trim()}
              className={`w-full py-2.5 md:py-3 text-white text-sm md:text-base font-semibold rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg ${
                !input.trim()
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : loading 
                    ? 'bg-gray-700' 
                    : 'bg-black hover:bg-black'
              }`}
              title=""
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 md:h-5 md:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-xs md:text-base">{detecting ? 'Detecting mode...' : 'Generating your result...'}</span>
                </>
              ) : (
                <>
                  <span className="font-bold">Transform &rarr;</span>
                </>
              )}
            </button>
            
            {/* Show promotional message only for unauthenticated visitors */}
            {promptMessage.show && (
              <div className="text-center mt-3 text-sm text-gray-600">
                <strong className="text-gray-900">{promptMessage.main}</strong>
                {promptMessage.secondary && (
                  <>
                    {' \u2022 '}
                    <button 
                      onClick={login} 
                      className="text-black hover:underline font-medium cursor-pointer"
                    >
                      {promptMessage.secondary}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* V37.44.8 — Free-access mode. "View pricing & credit packages"
              CTA removed. Underlying /pricing route is still reachable
              directly for internal accounting/admin, but no UI link
              exposes it to users. */}
          
          {/* V37.44.46 — History links moved here (b1) from the header per
              UX refinement. Shown only to authenticated users on Home.
              Text labels are identical on desktop and mobile. */}
          {isAuthenticated && (
            <div className="text-center mt-6 flex items-center justify-center gap-4 sm:gap-6">
              <button
                type="button"
                onClick={() => navigate('/prompts/projects')}
                className="text-gray-900 hover:text-black hover:underline underline-offset-4 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 rounded-sm"
                data-testid="home-project-history-link"
              >
                Project History
              </button>
              <span className="text-gray-300" aria-hidden="true">|</span>
              <button
                type="button"
                onClick={() => navigate('/prompts/bug-fixes')}
                className="text-gray-900 hover:text-black hover:underline underline-offset-4 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 rounded-sm"
                data-testid="home-bug-fix-history-link"
              >
                Bug Fix History
              </button>
            </div>
          )}

          {/* Privacy notice - plain text without wrapper */}
          {/* V37.44.46 — Nudged lower (mt-6) for clean spacing beneath the
              new history row. Mobile: mb-16 adds 64px spacing to footer. */}
          <div className="text-center mt-6 mb-16 md:mb-0">
            <div className="text-gray-600 text-xs font-medium">
              <span className="mr-2">{'\u{1F512}'}</span>
              Your prompts are private. Clear memory anytime.
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
      
    </div>
  );
};
